import { DurationUnit, UnitDisplayFormat } from '@m/constants/DateTimeFormat';
import { DATE_FORMAT } from '@m/constants/Time';
import { DateTime, Duration } from 'luxon';

import convertMilliseconds from '@m/utils/convertMilliseconds';

export type ConvertedPeriod = {
  interval: string;
  unit: 'minute' | 'hour' | 'day' | 'min' | 'hr';
};

export type UnitOverride = {
  day?: string;
  dayPlural?: string;
  hour?: string;
  hourPlural?: string;
  minute?: string;
  minutePlural?: string;
};

// Assume initial format is milliseconds
export function millisecondsToCleanPeriod(
  periodMilliseconds: number,
  options?: {
    useShortFormat?: boolean;
  },
): ConvertedPeriod {
  const periodMinutes = periodMilliseconds / 1000 / 60;
  if (periodMinutes >= 60) {
    const modMinutes = periodMinutes % 60;
    // periodMinutes are not in clean "hour" format. ex. 88, 54 minutes
    // clean hour would be something like 1, 1.25, 1.5 or 1.75 hours
    if (modMinutes !== 0 && modMinutes !== 15 && modMinutes !== 30 && modMinutes !== 45) {
      return {
        interval: `${periodMinutes}`,
        unit: options?.useShortFormat ? 'min' : 'minute',
      };
    }

    const periodHours = periodMinutes / 60;

    // periodHours are in "day" format
    if (periodHours % 24 === 0) {
      return {
        interval: `${periodHours / 24}`,
        unit: 'day',
      };
    }

    return {
      interval: `${periodHours}`,
      unit: options?.useShortFormat ? 'hr' : 'hour',
    };
  }

  return {
    interval: `${periodMinutes}`,
    unit: options?.useShortFormat ? 'min' : 'minute',
  };
}

/**
 * Convert milliseconds to readable format format. ex. 1 hour 30 minutes
 * @param duration - milliseconds
 * @param format - UnitDisplayFormat
 * @param separator - separate the times
 * @param onlyWholeDays - Duration is a whole day
 *                        Helpful to determine if we should show 1 day or 25 hours
 *                        Having days, hours, and minutes is a lot to fit in some components
 */
export function millisecondsToReadable({
  interval,
  format = UnitDisplayFormat.abbr,
  separator = ' ',
  onlyWholeDays = true,
  unitOverride,
  showZeroHours,
}: {
  format?: UnitDisplayFormat;
  interval: number;
  onlyWholeDays?: boolean;
  separator?: string;
  showZeroHours?: boolean;
  unitOverride?: UnitOverride;
}) {
  const {
    floorMinutes: minutes,
    floorHours: hours,
    floorDays: days,
    isWholeDay,
  } = convertMilliseconds(interval);

  let dayStr = null;
  let hourStr = null;
  let minStr = null;

  // Use smallest unit possible that's not 0 (unless 0 minutes)
  if (isWholeDay) {
    if (days !== 0) {
      return getDurationString({
        interval: days,
        unit: DurationUnit.day,
        format,
        unitOverride,
      });
    }
    if (hours !== 0) {
      return getDurationString({
        interval: hours,
        unit: DurationUnit.hour,
        format,
        unitOverride,
      });
    }
    return getDurationString({
      interval: minutes,
      unit: DurationUnit.minute,
      format,
      unitOverride,
    });
  }

  if (onlyWholeDays) {
    if (days > 0 || hours > 0) {
      hourStr = getDurationString({
        interval: days * 24 + hours,
        unit: DurationUnit.hour,
        format,
        unitOverride,
      });
    }
  } else {
    if (days > 0) {
      dayStr = getDurationString({
        interval: days,
        unit: DurationUnit.day,
        format,
        unitOverride,
      });
    }
    if (hours > 0) {
      hourStr = getDurationString({
        interval: hours,
        unit: DurationUnit.hour,
        format,
        unitOverride,
      });
    }
  }

  if (minutes > 0) {
    minStr = getDurationString({
      interval: minutes,
      unit: DurationUnit.minute,
      format,
      unitOverride,
    });
  }

  let result = dayStr;

  // Append hours
  if (result === null) {
    result = hourStr;
  } else if (hourStr !== null) {
    result = `${result}${separator}${hourStr}`;
  }

  // Append minutes
  if (result === null) {
    result = minStr;
  } else if (minStr !== null) {
    result = `${result}${separator}${minStr}`;
  }

  if (hourStr === null && showZeroHours) {
    // only used for VisitHistoryItem
    return `0h${separator}${result}`;
  }

  return result;
}

export function getDurationString({
  interval,
  unit,
  format,
  unitOverride,
}: {
  format: UnitDisplayFormat;
  interval: number;
  unit: DurationUnit;
  unitOverride?: UnitOverride;
}) {
  if (unit === DurationUnit.day) {
    if (unitOverride?.day) {
      if (interval !== 1 && unitOverride.dayPlural) {
        return `${interval}${unitOverride.dayPlural}`;
      }

      return `${interval}${unitOverride.day}`;
    }
    if (format === UnitDisplayFormat.short) {
      return `${interval}d`;
    }

    return `${interval} day${interval === 1 ? '' : 's'}`;
  }

  if (unit === DurationUnit.hour) {
    if (unitOverride?.hour) {
      if (interval !== 1 && unitOverride.hourPlural) {
        return `${interval}${unitOverride.hourPlural}`;
      }

      return `${interval}${unitOverride.hour}`;
    }
    if (format === UnitDisplayFormat.short) {
      return `${interval}h`;
    }

    if (format === UnitDisplayFormat.abbr) {
      return `${interval} hr${interval === 1 ? '' : 's'}`;
    }

    return `${interval} hour${interval === 1 ? '' : 's'}`;
  }

  if (unit === DurationUnit.minute) {
    if (unitOverride?.minute) {
      if (interval !== 1 && unitOverride.minutePlural) {
        return `${interval}${unitOverride.minutePlural}`;
      }

      return `${interval}${unitOverride.minute}`;
    }
    if (format === UnitDisplayFormat.short) {
      return `${interval}m`;
    }

    if (format === UnitDisplayFormat.abbr) {
      return `${interval} min${interval === 1 ? '' : 's'}`;
    }

    return `${interval} minute${interval === 1 ? '' : 's'}`;
  }

  // Should never get here
  return '';
}

/**
 * Prefixes with today or tomorrow if valid. Formats with Luxon
 *
 * ex:
 * Today, April 1
 * Tomorrow, April 1
 * April 1
 */
export function prefixRelativeDate(
  timestamp: number,
  format: string,
  additonalOptions?: {
    /**
     * If disabled, the relative date will not be prefixed
     */
    disableToday?: boolean;
    disableTomorrow?: boolean;
    todayPrefix?: string;
    tomorrowPrefix?: string;
    unprefixableFormat?: string;
  },
) {
  const {
    disableToday = false,
    disableTomorrow = false,
    unprefixableFormat = null,
    todayPrefix = 'Today, ',
    tomorrowPrefix = 'Tomorrow, ',
  } = additonalOptions || {};

  const today = DateTime.fromMillis(Date.now());
  const targetDate = DateTime.fromMillis(timestamp);
  const comparableDate = targetDate.toFormat(DATE_FORMAT);
  const targetDateFormatted = targetDate.toFormat(format);

  if (today.toFormat(DATE_FORMAT) === comparableDate) {
    return disableToday ? targetDateFormatted : `${todayPrefix}${targetDateFormatted}`;
  }

  if (today.plus({ day: 1 }).toFormat(DATE_FORMAT) === comparableDate) {
    return disableTomorrow ? targetDateFormatted : `${tomorrowPrefix}${targetDateFormatted}`;
  }

  if (unprefixableFormat) {
    return DateTime.fromMillis(timestamp).toFormat(unprefixableFormat);
  }
  return targetDateFormatted;
}

export function parkingDurationEndLabel(timestamp: number, format: string = 'LLL d') {
  const today = DateTime.now().toLocaleString();
  const tomorrow = DateTime.now().plus({ days: 1 }).toLocaleString();
  const timeUserSelected = DateTime.fromMillis(timestamp).toLocaleString();

  if (timeUserSelected === today) {
    return 'End time';
  }

  if (timeUserSelected === tomorrow) {
    return `Tomorrow, ${DateTime.fromMillis(timestamp).toFormat(format)}`;
  }

  return DateTime.fromMillis(timestamp).toFormat(format);
}

export function isToday(timestamp: number) {
  const today = DateTime.now().toLocaleString();
  const targetTime = DateTime.fromMillis(timestamp).toLocaleString();
  return targetTime === today;
}

export function convertMinutesToHours(minutes: number) {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  return {
    hours,
    minutes: remainingMinutes,
  };
}

export function convertMinutesToReadableTime(
  minutes: number,
  options?: { pluralizeMinutes?: boolean },
) {
  const duration = Duration.fromObject({ minutes });
  const hours = Math.floor(duration.as('hours'));
  const remainingMinutes = duration.minutes % 60;
  const pluralizeMinutes = options?.pluralizeMinutes ?? false;

  const formattedStr =
    hours > 0
      ? `${hours} hr${hours > 1 ? 's' : ''} ${
          remainingMinutes > 0
            ? `${remainingMinutes} min${pluralizeMinutes && remainingMinutes > 1 ? 's' : ''}`
            : ''
        }`
      : `${remainingMinutes} min${pluralizeMinutes && remainingMinutes > 1 ? 's' : ''}`;

  return formattedStr.trim();
}
